import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { Dispute } from '../data-models/dispute.model';
import { DisputesFilters } from '../request-models/disputes-filters.model';
import { ResolveDisputeRequest } from '../request-models/resolve-dispute-request.model';
import { toDisputesQuery, toResolveDisputeRequest } from '../utils/dispute-request-utils';
import { isRecord } from '../utils/is-record';

/** Resolution failures the dispute page explains specifically (hub `RESOLVE_ERROR_RESPONSES`). */
export const DISPUTE_INVALID_RESOLUTION = 'INVALID_RESOLUTION';
export const DISPUTE_STRIPE_REFUND_FAILED = 'STRIPE_REFUND_FAILED';

/**
 * Marketplace payment disputes: `/admin/disputes` (hub `admin-disputes-controller.ts`). The list is paged with
 * `limit` / `offset` and returns a plain array. Resolution failures arrive as an `ApiError` with a `code`
 * (`INVALID_RESOLUTION`, `STRIPE_REFUND_FAILED`, `DISPUTE_ALREADY_RESOLVED`, …).
 */
@Injectable({ providedIn: 'root' })
export class DisputeService extends BaseService<Dispute> {
  constructor() {
    super('admin/disputes', Dispute);
  }

  /** GET /admin/disputes, most recent first. */
  listDisputes(filters: DisputesFilters = {}): Observable<Dispute[]> {
    return this.getMany({ query: toDisputesQuery(filters) });
  }

  /** GET /admin/disputes/:id. Emits `null` when the hub returns no data. */
  getDispute(disputeId: string): Observable<Dispute | null> {
    return this.get(disputeId);
  }

  /** POST /admin/disputes/:id/resolve. Emits the updated dispute, or `null` when the hub sent none. */
  resolveDispute(disputeId: string, body: ResolveDisputeRequest): Observable<Dispute | null> {
    return this.request('POST', { id: disputeId, path: 'resolve' }, toResolvedDispute, toResolveDisputeRequest(body));
  }
}

function toResolvedDispute(data: unknown): Dispute | null {
  const dispute = isRecord(data) ? data['dispute'] : undefined;
  return isRecord(dispute) ? new Dispute(dispute) : null;
}
